import { z } from 'zod';
import type { CompoundQueryMode, FeatureArea, NavbarItem, PlannerMode } from '@trinity/features-registry';
import {
  accountAutocompleteEndpoint,
  accountSearchEndpoint,
  additionalAgentContext,
  agentDisclaimer,
  applicationTooltip,
  autoTagFiles,
  availableWidgets,
  browserTabName,
  buildFlatFeaturesModel,
  chatAtMentionFilter,
  chatAuraIframesEnabled,
  chatContextSelector,
  chatExpirationHours,
  chatGuidelines,
  chatInputAttachmentsEnabled,
  chatInputAttachmentAllowedMimeTypes,
  chatInputAttachmentCustomS3Enabled,
  chatInputAttachmentS3Config,
  chatInteractionAvailableModes,
  chatInteractionDefaultMode,
  chatLiveAvatar,
  chatLiveAvatarId,
  chatLiveAvatarTtsConfig,
  chatReadAloud,
  chatReadAloudTtsConfig,
  chatVoiceInput,
  chatWelcomeScreen,
  chatWelcomeScreenContent,
  COMPOUND_QUERY_MODES,
  PLANNER_MODES,
  dataAgentAiContextLayer,
  dataAgentCompoundMode,
  dataAgentDynamicInsights,
  webSearchGoogleEnabled,
  webSearchCustomSearchEnabled,
  webSearchPerplexityEnabled,
  webSearchIngestionPolicy,
  deleteChatsOnSessionEnd,
  disableChatInputWithLandingCards,
  engagementFilter,
  engagementResponseLabels,
  faqChatContextIdentifier,
  FEATURES,
  FeatureAreaSchema,
  FeatureType,
  followUps,
  formWorkflow,
  formWorkflows,
  hcpAlertsTool,
  hcpAutocompleteEndpoint,
  hcpClinicalTrials,
  hcpConferencePresence,
  hcpContactInformation,
  hcpInteractionsSummary,
  hcpKeyDiscussionQuestions,
  hcpKeyInsights,
  hcpLeaderTopics,
  hcpOpensearchIndex,
  hcpProfiles,
  hcpPublications,
  hcpSearchEndpoint,
  hcpSchedulingAssistant,
  homeButton,
  homeScreen,
  identificationPrompt,
  inputGuardrailsPrompt,
  webSearchCompliancePrompt,
  isJsonDef,
  maxUserChatQueries,
  NAVBAR_ITEM_DEFAULTS,
  navbarItems,
  predictiveScheduling,
  repAutocompleteEndpoint,
  repSearchEndpoint,
  resolveNavbarItems,
  repTwoMinuteSummaryTool,
  smartRouting,
  smartRoutingCustomColumns,
  smartRoutingEligibleEntities,
  smartRoutingEnabled,
  smartRoutingOpensearchIndex,
  speechServicesProvider,
  supportIcon,
  tableAccountViewProfileLinks,
  tableHcpViewProfileLinks,
  useNewExperience,
  viewAccountProfileTool,
  viewHcpProfileTool,
  voiceMode,
  workspaceBrands,
  workspaces,
} from '@trinity/features-registry';

export {
  AGENT_CHART_TOOLS,
  AGENT_CHART_TOOL_KEYS,
  normalizeAgentChartToolKeys,
  type AgentChartToolKey,
} from '@trinity/features-registry';

export {
  FeatureAreaSchema,
  FeatureType,
  COMPOUND_QUERY_MODES,
  PLANNER_MODES,
  NAVBAR_ITEM_DEFAULTS,
  resolveNavbarItems,
};
export type { FeatureArea, CompoundQueryMode, PlannerMode, NavbarItem };

const BooleanFeature = z.object({
  type: z.literal(FeatureType.Boolean),
  value: z.boolean(),
});
export type BooleanFeature = z.infer<typeof BooleanFeature>;

const StringFeature = z.object({
  type: z.literal(FeatureType.String),
  value: z.string(),
});
export type StringFeature = z.infer<typeof StringFeature>;

const NumberFeature = z.object({
  type: z.literal(FeatureType.Number),
  value: z.number(),
});
export type NumberFeature = z.infer<typeof NumberFeature>;

const MarkdownFeature = z.object({
  type: z.literal(FeatureType.Markdown),
  value: z.string(),
});
export type MarkdownFeature = z.infer<typeof MarkdownFeature>;

const MultiSelectFeature = z.object({
  type: z.literal(FeatureType.MultiSelect),
  value: z.array(z.string()),
});
export type MultiSelectFeature = z.infer<typeof MultiSelectFeature>;

const JsonFeature = z.object({
  type: z.literal(FeatureType.Json),
  value: z.unknown(),
});
export type JsonFeature = z.infer<typeof JsonFeature>;

const StringListFeature = z.object({
  type: z.literal(FeatureType.StringList),
  value: z.array(z.string()),
});
export type StringListFeature = z.infer<typeof StringListFeature>;

export const FeatureValueSchema = z.discriminatedUnion('type', [
  BooleanFeature,
  StringFeature,
  NumberFeature,
  MarkdownFeature,
  MultiSelectFeature,
  JsonFeature,
  StringListFeature,
]);
export type FeatureValue =
  | { type: typeof FeatureType.Boolean; value: boolean }
  | { type: typeof FeatureType.String; value: string }
  | { type: typeof FeatureType.Number; value: number }
  | { type: typeof FeatureType.Markdown; value: string }
  | { type: typeof FeatureType.MultiSelect; value: string[] }
  | { type: typeof FeatureType.Json; value: unknown }
  | { type: typeof FeatureType.StringList; value: string[] };

const BlankValidation = <FT extends FeatureType>(ft: FT) => {
  return z.object({
    type: z.literal(ft),
  });
};

const BooleanValidation = BlankValidation(FeatureType.Boolean);
export type BooleanValidation = z.infer<typeof BooleanValidation>;

const StringValidation = z.object({
  type: z.literal(FeatureType.String),
  validValues: z.array(z.string()).optional(),
});
export type StringValidation = z.infer<typeof StringValidation>;

const NumberValidation = BlankValidation(FeatureType.Number);
export type NumberValidation = z.infer<typeof NumberValidation>;

const MarkdownValidation = BlankValidation(FeatureType.Markdown);
export type MarkdownValidation = z.infer<typeof MarkdownValidation>;

const MultiSelectValidation = z.object({
  type: z.literal(FeatureType.MultiSelect),
  validValues: z.array(z.string()),
});
export type MultiSelectValidation = z.infer<typeof MultiSelectValidation>;

const JsonValidation = BlankValidation(FeatureType.Json);
export type JsonValidation = z.infer<typeof JsonValidation>;

const StringListValidation = BlankValidation(FeatureType.StringList);
export type StringListValidation = z.infer<typeof StringListValidation>;

export const FeatureValidationSchema = z.discriminatedUnion('type', [
  BooleanValidation,
  StringValidation,
  NumberValidation,
  MarkdownValidation,
  MultiSelectValidation,
  JsonValidation,
  StringListValidation,
]);
export type FeatureValidation =
  | { type: typeof FeatureType.Boolean }
  | { type: typeof FeatureType.String; validValues?: string[] }
  | { type: typeof FeatureType.Number }
  | { type: typeof FeatureType.Markdown }
  | { type: typeof FeatureType.MultiSelect; validValues: string[] }
  | { type: typeof FeatureType.Json }
  | { type: typeof FeatureType.StringList };

export const FeatureValidationSchemaMap = {
  [FeatureType.Boolean]: BooleanValidation,
  [FeatureType.String]: StringValidation,
  [FeatureType.Number]: NumberValidation,
  [FeatureType.Markdown]: MarkdownValidation,
  [FeatureType.MultiSelect]: MultiSelectValidation,
  [FeatureType.Json]: JsonValidation,
  [FeatureType.StringList]: StringListValidation,
};

export const FeatureDetailSchema = z
  .object({
    propName: z.string(),
    displayName: z.string(),
    description: z.string(),
    category: z.object({
      id: z.number(),
      name: z.string(),
    }),
    areas: z.array(FeatureAreaSchema),
    source: z
      .object({
        name: z.string(),
      })
      .nullable(),
    feature: FeatureValueSchema,
    validation: FeatureValidationSchema,
  })
  .refine((a) => a.feature.type === a.validation.type, {
    error: 'Feature type and validation type do not match',
  });
export type FeatureDetail = z.infer<typeof FeatureDetailSchema>;

export const homePageSchema = z.enum(['chat', 'workspace', 'analyst-main']);
export type HomePage = z.infer<typeof homePageSchema>;

// ─── JSON feature schemas ─────────────────────────────────────────────────────

export const FormWorkflowItemSchema = z.object({ workflowKey: z.string() });
export type FormWorkflowItem = z.infer<typeof FormWorkflowItemSchema>;
export const FormWorkflowsSchema = z.array(FormWorkflowItemSchema);
export type FormWorkflows = z.infer<typeof FormWorkflowsSchema>;

/**
 * Map of propName -> Zod schema for JSON-typed features. Derived from the
 * registry: every JsonDef contributes its own schema, replacing the legacy
 * hand-maintained map.
 */
export const JsonFeatureSchemaRegistry: Partial<Record<string, z.ZodSchema>> = Object.fromEntries(
  FEATURES.filter(isJsonDef).map((def) => [def.propName, def.schema as z.ZodSchema])
);

/** Map of propName -> read-time normalizer for JSON-typed features that define one. */
const JsonFeatureReadNormalizers: Partial<Record<string, (value: unknown) => unknown>> = Object.fromEntries(
  FEATURES.flatMap((def) => (isJsonDef(def) && def.normalizeStored ? [[def.propName, def.normalizeStored] as const] : []))
);

// ─── Flat agent features model ────────────────────────────────────────────────

const flatAgentFeaturesDefs = [
  smartRoutingEnabled,
  smartRoutingOpensearchIndex,
  smartRoutingEligibleEntities,
  smartRoutingCustomColumns,
  formWorkflows,
  availableWidgets,
  tableHcpViewProfileLinks,
  tableAccountViewProfileLinks,
  additionalAgentContext,
  agentDisclaimer,
  identificationPrompt,
  inputGuardrailsPrompt,
  webSearchCompliancePrompt,
  viewAccountProfileTool,
  viewHcpProfileTool,
  hcpAlertsTool,
  repTwoMinuteSummaryTool,
  dataAgentDynamicInsights,
  dataAgentAiContextLayer,
  webSearchGoogleEnabled,
  webSearchCustomSearchEnabled,
  webSearchPerplexityEnabled,
  webSearchIngestionPolicy,
] as const;

export const flatAgentFeaturesModel = buildFlatFeaturesModel(flatAgentFeaturesDefs);
export type FlatAgentFeatures = z.infer<typeof flatAgentFeaturesModel>;

const flatFeaturesDefs = [
  autoTagFiles,
  workspaces,
  workspaceBrands,
  hcpProfiles,
  chatVoiceInput,
  chatReadAloud,
  chatWelcomeScreen,
  chatLiveAvatar,
  chatLiveAvatarId,
  chatLiveAvatarTtsConfig,
  chatReadAloudTtsConfig,
  chatInteractionAvailableModes,
  chatInteractionDefaultMode,
  smartRouting,
  smartRoutingEnabled,
  smartRoutingOpensearchIndex,
  smartRoutingEligibleEntities,
  smartRoutingCustomColumns,
  voiceMode,
  deleteChatsOnSessionEnd,
  hcpSchedulingAssistant,
  followUps,
  predictiveScheduling,
  chatGuidelines,
  chatExpirationHours,
  speechServicesProvider,
  engagementResponseLabels,
  engagementFilter,
  supportIcon,
  chatAuraIframesEnabled,
  hcpContactInformation,
  hcpInteractionsSummary,
  hcpKeyDiscussionQuestions,
  hcpKeyInsights,
  hcpLeaderTopics,
  hcpClinicalTrials,
  hcpPublications,
  hcpConferencePresence,
  hcpAutocompleteEndpoint,
  hcpSearchEndpoint,
  hcpOpensearchIndex,
  browserTabName,
  maxUserChatQueries,
  disableChatInputWithLandingCards,
  homeScreen,
  homeButton,
  useNewExperience,
  faqChatContextIdentifier,
  navbarItems,
  chatContextSelector,
  applicationTooltip,
  chatWelcomeScreenContent,
  formWorkflows,
  formWorkflow,
  availableWidgets,
  tableHcpViewProfileLinks,
  tableAccountViewProfileLinks,
  additionalAgentContext,
  agentDisclaimer,
  identificationPrompt,
  inputGuardrailsPrompt,
  chatAtMentionFilter,
  chatInputAttachmentsEnabled,
  chatInputAttachmentAllowedMimeTypes,
  chatInputAttachmentCustomS3Enabled,
  chatInputAttachmentS3Config,
  repAutocompleteEndpoint,
  repSearchEndpoint,
  accountAutocompleteEndpoint,
  accountSearchEndpoint,
  dataAgentCompoundMode,
] as const;

export const flatFeaturesModel = buildFlatFeaturesModel(flatFeaturesDefs);
export type FlatFeatures = z.infer<typeof flatFeaturesModel>;

const ParserMap = {
  [FeatureType.Boolean]: z.enum(['true', 'false']).transform((value) => value === 'true'),
  [FeatureType.String]: z.string(),
  [FeatureType.Number]: z.coerce.number(),
  [FeatureType.Markdown]: z.string(),
  [FeatureType.MultiSelect]: z
    .string()
    .transform((value) => JSON.parse(value) as unknown)
    .pipe(z.array(z.string())),
  [FeatureType.StringList]: z
    .string()
    .transform((value) => JSON.parse(value) as unknown)
    .pipe(z.array(z.string())),
};

type ParseResult =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      value: FeatureValue;
    };

type ParseFeatureValueOptions = {
  /** Stored value being read back (not a write): applies the feature's `normalizeStored`, if any. */
  forRead?: boolean;
};

function parseJsonFeatureValue(value: string, propName: string | undefined, forRead: boolean | undefined): ParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return { success: false as const, message: `Invalid JSON value for feature` };
  }

  const normalize = forRead && propName !== undefined ? JsonFeatureReadNormalizers[propName] : undefined;
  const normalized = normalize ? normalize(parsed) : parsed;

  const registeredSchema = propName !== undefined ? JsonFeatureSchemaRegistry[propName] : undefined;
  if (registeredSchema) {
    const result = registeredSchema.safeParse(normalized);
    if (!result.success) {
      return { success: false as const, message: result.error.message };
    }
  }

  return { success: true, value: { type: FeatureType.Json, value: normalized } as FeatureValue };
}

export function parseFeatureValue(
  value: string,
  type: FeatureType,
  propName?: string,
  options: ParseFeatureValueOptions = {}
): ParseResult {
  if (type === FeatureType.Json) {
    return parseJsonFeatureValue(value, propName, options.forRead);
  }

  const parser = ParserMap[type];

  const result = parser.safeParse(value);
  if (!result.success) {
    return {
      success: false as const,
      message: result.error.message,
    };
  } else {
    return {
      success: true,
      value: {
        value: parser.parse(value),
        type,
      } as FeatureValue,
    };
  }
}

type ValidationParseResult =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      value: FeatureValidation;
    };

export function parseFeatureValidations(value: object, type: FeatureType): ValidationParseResult {
  const parser = FeatureValidationSchemaMap[type];

  const result = parser.safeParse({ ...value, type });
  if (!result.success) {
    return {
      success: false as const,
      message: result.error.message,
    };
  } else {
    return {
      success: true,
      value: result.data,
    };
  }
}

export function stringifyFeatureValue(featureValue: FeatureValue): string {
  switch (featureValue.type) {
    case FeatureType.Boolean:
      return featureValue.value ? 'true' : 'false';
    case FeatureType.String:
      return featureValue.value;
    case FeatureType.Number:
      return featureValue.value.toString();
    case FeatureType.Markdown:
      return featureValue.value;
    case FeatureType.MultiSelect:
      return JSON.stringify(featureValue.value);
    case FeatureType.Json:
      return JSON.stringify(featureValue.value);
    case FeatureType.StringList:
      return JSON.stringify(featureValue.value);
    default:
      throw new Error(`Unknown feature type: ${(featureValue as FeatureValue).type}`);
  }
}
